import { useState } from "react";
import { Link } from "react-router";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Card,
  EmptyState,
  Mono,
  PageHeader,
  Table,
  TableSkeleton,
  Td,
  Th,
  Tag,
} from "../components/ui";
import { formatDateTime, formatRelative } from "../lib/format";
import { useFleetTunnels } from "../lib/queries";
import type { FleetTunnel } from "../lib/types";

const LIMIT = 25;

/**
 * Every private connection on the fleet.
 *
 * Read-only, and this is the whole operator surface for the feature. Until it
 * existed, "my connection is down" was answered by reading `org_tunnels` out of
 * Postgres by hand — the same place the affiliate programme was before it got a
 * screen.
 *
 * The peer configuration is not here and never will be. It is sealed at rest,
 * it opens a door into a customer's own network, and no question an operator
 * has needs it — which is also why the API route this reads selects no such
 * column rather than trusting this page not to render one.
 */
export function TunnelsPage() {
  const [offset, setOffset] = useState(0);
  const tunnels = useFleetTunnels({ limit: LIMIT, offset });

  const rows = tunnels.data?.data ?? [];
  const total = tunnels.data?.total ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tunnels"
        description="WireGuard tunnels from tenant workloads into their own networks. Billed at the tunnel add-on's rate."
      />

      {tunnels.error && <Alert>{tunnels.error.message}</Alert>}

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[13px] text-fg-subtle">
          {total} tunnel{total === 1 ? "" : "s"} across the fleet
        </span>
      </div>

      <Card>
        {tunnels.isPending ? (
          <TableSkeleton columns={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No tunnels"
            description="No tenant has created one yet."
          />
        ) : (
          <Table
            head={
              <>
                {/*
                  Priorities drop the columns whose fact is reachable elsewhere
                  first. Region and server are on the organization's own page;
                  the tenant, the connection and whether it is wired up are the
                  three an operator is scanning for and none of them go.
                */}
                <Th>Organization</Th>
                <Th>Tunnel</Th>
                <Th priority="sm">Reaches</Th>
                <Th priority="md">Last seen carrying traffic</Th>
                <Th priority="lg">Region</Th>
                <Th priority="md">Server</Th>
              </>
            }
          >
            {rows.map((tunnel) => (
              <tr key={tunnel.id}>
                <Td>
                  <Link
                    to={`/organizations/${tunnel.organization.id}`}
                    className="text-fg hover:underline"
                  >
                    {tunnel.organization.name}
                  </Link>
                </Td>
                <Td>
                  <div className="text-fg">{tunnel.name}</div>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {tunnel.state !== "running" && <Tag>{tunnel.state}</Tag>}
                    {/*
                      The state could not be confirmed, so it is our record and
                      may be old. Worth a tag of its own here rather than a
                      dimmed badge: an operator reading this column is deciding
                      whether to trust it against what a customer is telling
                      them.
                    */}
                    {tunnel.stale && <Tag>unconfirmed</Tag>}
                    {/*
                      Not billed and carrying nothing — the difference an
                      operator needs when a tenant asks why a connection they
                      created does nothing.
                    */}
                    {!tunnel.provisioned && <Tag tone="bad">not created</Tag>}
                  </div>
                </Td>
                <Td priority="sm">
                  <div className="text-fg">
                    {tunnel.targets} address{tunnel.targets === 1 ? "" : "es"}
                  </div>
                  {/*
                    What the tenant's apps connect to. The remote addresses
                    behind it are theirs and are not listed here: a count
                    answers "is anything wired up", which is the operator's
                    question, without putting a map of somebody's internal
                    network on a staff screen.
                  */}
                  {tunnel.internalHost && (
                    <Mono className="text-[11px] text-fg-subtle">{tunnel.internalHost}</Mono>
                  )}
                </Td>
                <Td priority="md">
                  <HandshakeCell tunnel={tunnel} />
                </Td>
                <Td priority="lg">
                  {tunnel.region ?? <span className="text-fg-subtle">—</span>}
                </Td>
                <Td priority="md">
                  {tunnel.server ? (
                    <Link to={`/servers/${tunnel.server.id}`} className="text-fg hover:underline">
                      {tunnel.server.name ?? tunnel.server.id}
                    </Link>
                  ) : (
                    <span className="text-fg-subtle">unplaced</span>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </Card>
    </div>
  );
}

/**
 * When the platform last saw the tunnel carrying traffic.
 *
 * Refreshed only when somebody opens the connection in the customer dashboard,
 * so an old timestamp means nobody has looked recently rather than that the
 * tunnel is down. Saying that outright is the difference between this column
 * being useful and being read as an outage.
 */
function HandshakeCell({ tunnel }: { tunnel: FleetTunnel }) {
  if (!tunnel.provisioned) return <span className="text-fg-subtle">never created</span>;
  if (!tunnel.lastHandshakeAt) {
    return (
      <>
        <div className="text-fg-subtle">never seen</div>
        <div className="text-[11px] text-fg-subtle">created {formatRelative(tunnel.createdAt)}</div>
      </>
    );
  }
  return (
    <>
      <div className="text-fg">{formatRelative(tunnel.lastHandshakeAt)}</div>
      <div className="text-[11px] text-fg-subtle" title={formatDateTime(tunnel.lastHandshakeAt)}>
        when the tenant last looked
      </div>
    </>
  );
}
